import { useRef, useState, useEffect, useLayoutEffect, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchQuery, useSearchPage, useSearchSort } from '../contexts/SearchContext';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import { useIntl, defineMessages } from 'react-intl';
// import { useDispatch, useSelector } from 'react-redux';
// import { RootState } from '../redux/store';
// import { setHasSeenSearchPulse } from '../redux/features/uiSlice';
import { usePushIndexUiState } from '../hooks/usePushIndexUiState';
import { useSearchFilters } from '../contexts/SearchContext';
import { useFacets } from '../contexts/FacetsContext';
import { buildFacetAutocompleteOptions, FacetAutocompleteOption, filterFacetAutocompleteOptions } from '../utils/algolia/facet-autocomplete';
import { useThrottledResize } from '../hooks/useThrottledResize';
import { trackSearchSuggestion } from '../utils/analytics/analytics';

export type AutocompleteHandle = {
  close: () => void;
};

type AutocompleteProps = {
  className?: string;
  placeholder?: string;
  popUp?: boolean; // If true, popover appears above the input (for bottom nav)
  onOpen?: () => void; // Callback when autocomplete opens
};

const FACET_SUGGESTION_LIMIT = 20;

const facetAttributeMessages = defineMessages({
  location: { id: 'facets.location', defaultMessage: 'Location', description: 'Label for location facet in autocomplete suggestions' },
  remoteness: { id: 'facets.remoteness', defaultMessage: 'Work setup', description: 'Label for remoteness/work model facet in autocomplete suggestions' },
  companyIndustry: { id: 'facets.companyIndustry', defaultMessage: 'Industry', description: 'Label for company industry facet in autocomplete suggestions' },
  companyId: { id: 'facets.company', defaultMessage: 'Company', description: 'Label for company facet in autocomplete suggestions' },
  jobType: { id: 'facets.jobType', defaultMessage: 'Job type', description: 'Label for job type facet in autocomplete suggestions' },
  normalisedRoleLevel: { id: 'facets.roleLevel', defaultMessage: 'Role level', description: 'Label for role level facet in autocomplete suggestions' },
  normalisedTitle: { id: 'facets.role', defaultMessage: 'Role', description: 'Label for role/job title facet in autocomplete suggestions' },
  requiredLanguages: { id: 'facets.language', defaultMessage: 'Language', description: 'Label for required language facet in autocomplete suggestions' },
  benefits: { id: 'facets.benefits', defaultMessage: 'Benefit', description: 'Label for benefits facet in autocomplete suggestions' },
  skillsAndTechnologies: { id: 'facets.skills', defaultMessage: 'Skill', description: 'Label for skills and technologies facet in autocomplete suggestions' },
  field: { id: 'facets.field', defaultMessage: 'Field', description: 'Label for job field/domain facet in autocomplete suggestions' },
  educationLevel: { id: 'facets.education', defaultMessage: 'Education', description: 'Label for education level facet in autocomplete suggestions' },
  peopleManagementTier: { id: 'facets.management', defaultMessage: 'Leadership', description: 'Label for management/leadership tier facet in autocomplete suggestions' },
});

export const Autocomplete = forwardRef<AutocompleteHandle, AutocompleteProps>(
  ({ className, placeholder, popUp = false, onOpen }, ref) => {
  const { query: contextQuery } = useSearchQuery();
  const { page } = useSearchPage();
  const { sort } = useSearchSort();
  const { pushIndexUiState } = usePushIndexUiState();
  const { filters } = useSearchFilters();
  const filtersObj = (typeof filters === 'object' && filters) ? (filters as Record<string, string[]>) : {};
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [facetSuggestions, setFacetSuggestions] = useState<FacetAutocompleteOption[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [popoverPosition, setPopoverPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    /** Pop-up (mobile) panel height cap; derived from space above the input (visual viewport). */
    maxHeightPx: 384,
  });
  const [isMobile, setIsMobile] = useState(false);
  const intl = useIntl();
  const { facetsData } = useFacets();

  const attributeLabels = useMemo(() => {
    const entries: Record<string, string> = {};
    (Object.keys(facetAttributeMessages) as Array<keyof typeof facetAttributeMessages>).forEach((key) => {
      entries[key] = intl.formatMessage(facetAttributeMessages[key]);
    });
    return entries;
  }, [intl]);

  const facetOptions = useMemo(
    () => buildFacetAutocompleteOptions(facetsData, attributeLabels),
    [facetsData, attributeLabels]
  );
  // const dispatch = useDispatch();
  // const hasSeenSearchPulse = useSelector((state: RootState) => state.ui.hasSeenSearchPulse);
  const [pulse, setPulse] = useState(false);
  
  // Local input state - updates immediately for responsive UI
  const [inputValue, setInputValue] = useState(contextQuery);
  
  // Debounce timer for search
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  // Track the query we're about to push to prevent unnecessary syncs
  // When we push a query, we set it here. When contextQuery updates to match, we skip syncing.
  const pendingQueryRef = useRef<string | null>(null);
  // Track if input was focused before search update (to maintain keyboard on mobile)
  const wasFocusedBeforeSearchRef = useRef(false);
  // Track when we're performing a programmatic search update to prevent blur from closing popover
  const isPerformingSearchUpdateRef = useRef(false);
  // Store selection state to restore after re-renders
  const selectionStateRef = useRef<{ start: number; end: number } | null>(null);
  
  // Sync local input value with context query when it changes externally (e.g., from URL)
  // Skip syncing if we're in the middle of a programmatic search update to prevent focus loss on mobile
  useEffect(() => {
    // Don't sync if we're performing a programmatic search update
    // This prevents the re-render from causing focus loss on mobile
    if (!isPerformingSearchUpdateRef.current) {
      const isInputFocused = document.activeElement === inputRef.current;
      
      // If pendingQueryRef matches contextQuery, it means the context updated from our own push
      // In that case, our local inputValue is already correct (or user has typed more), so we don't need to sync
      if (pendingQueryRef.current === contextQuery) {
        // Our own push completed - don't overwrite user input if they've typed more
        // Only sync if inputValue doesn't start with what we pushed (meaning it was changed externally)
        // But if input is focused, never overwrite (user is actively typing)
        if (!isInputFocused && inputValue !== contextQuery && !inputValue.startsWith(contextQuery)) {
          setInputValue(contextQuery);
        }
      } else {
        // Context changed from an external source (URL, etc.), so we should sync
        // BUT: Only sync if the input is NOT currently focused and being typed in
        // This prevents overwriting user input while they're actively typing
        if (!isInputFocused) {
          setInputValue(contextQuery);
        }
      }
    }
    // Clear the pending ref after checking (whether we synced or not)
    pendingQueryRef.current = null;
  }, [contextQuery]);
  
  // Use layout effect to restore focus and selection synchronously after re-renders
  // This runs synchronously before the browser paints, preventing visible focus loss
  // Depend on contextQuery so it runs when the search context updates (after pushIndexUiState)
  // This only runs after the debounced search update completes, not on every keystroke
  useLayoutEffect(() => {
    // If we're performing a search update and should maintain focus, restore it immediately
    if (isPerformingSearchUpdateRef.current && wasFocusedBeforeSearchRef.current && inputRef.current) {
      // Restore focus synchronously before paint
      if (document.activeElement !== inputRef.current) {
        inputRef.current.focus();
      }
      
      // Restore selection state if we have it stored (restore cursor position)
      if (selectionStateRef.current && inputRef.current.setSelectionRange) {
        try {
          inputRef.current.setSelectionRange(
            selectionStateRef.current.start,
            selectionStateRef.current.end
          );
        } catch (e) {
          // setSelectionRange can fail if input is not focused, but we just focused it above
          // So this should rarely happen, but catch just in case
        }
      }
    }
  }, [contextQuery]); // Run when context query changes (after debounced search updates)

  // useEffect(() => {
  //   if (!hasSeenSearchPulse) {
  //     setPulse(true);
  //     const timeout = setTimeout(() => {
  //       setPulse(false);
  //       dispatch(setHasSeenSearchPulse(true));
  //     }, 1200 * 3 + 100); // 3 pulses + buffer
  //     return () => clearTimeout(timeout);
  //   }
  // }, [hasSeenSearchPulse, dispatch]);

  const refreshFacetSuggestions = useCallback(
    (q: string) => {
      // If query is empty, show all facets (limited). Otherwise, filter by query.
      const suggestions = q.trim().length > 0
        ? filterFacetAutocompleteOptions(facetOptions, q, FACET_SUGGESTION_LIMIT)
        : filterFacetAutocompleteOptions(facetOptions, '', FACET_SUGGESTION_LIMIT);
      setFacetSuggestions(suggestions);
    },
    [facetOptions]
  );

  useEffect(() => {
    refreshFacetSuggestions(inputValue);
  }, [facetOptions, inputValue, refreshFacetSuggestions]);
  
  // Debounced search update (delays actual search/navigation)
  const debouncedSearchUpdate = useCallback((searchQuery: string) => {
    // Clear existing timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    // Set new timer - wait 500ms after user stops typing
    searchDebounceRef.current = setTimeout(() => {
      // Check focus state RIGHT BEFORE the search update (not when debounce was set)
      // This ensures we capture the actual state at the moment the search fires
      const isInputFocused = document.activeElement === inputRef.current;
      wasFocusedBeforeSearchRef.current = isInputFocused;
      
      // Store selection state if input is focused (capture cursor position)
      if (isInputFocused && inputRef.current) {
        try {
          const start = inputRef.current.selectionStart ?? inputRef.current.value.length;
          const end = inputRef.current.selectionEnd ?? inputRef.current.value.length;
          selectionStateRef.current = { start, end };
        } catch (e) {
          // selectionStart/End might not be available in all cases
          selectionStateRef.current = null;
        }
      } else {
        selectionStateRef.current = null;
      }
      
      const shouldMaintainFocus = isInputFocused;
      
      // Mark that we're performing a programmatic search update BEFORE any state updates
      // This prevents the blur handler and sync effect from interfering
      isPerformingSearchUpdateRef.current = true;
      
      // Maintain focus state before the update to prevent popover from closing
      if (shouldMaintainFocus && inputRef.current) {
        setIsFocused(true);
        setIsOpen(true);
      }
      
      // Mark this query as pending so we don't sync it back unnecessarily
      pendingQueryRef.current = searchQuery;
      
      // Perform the search update
      // This will trigger re-renders, but useLayoutEffect will restore focus synchronously
      pushIndexUiState({ query: searchQuery, refinementList: filters as any, page: 0 });
      
      // Clear the flag after a short delay to allow re-renders to complete
      // The useLayoutEffect will handle the actual focus restoration synchronously
      setTimeout(() => {
        // Double-check focus is maintained after all re-renders (final fallback)
        if (shouldMaintainFocus && inputRef.current && document.activeElement !== inputRef.current) {
          // Final fallback: restore focus if it was lost
          inputRef.current.focus();
          if (selectionStateRef.current && inputRef.current.setSelectionRange) {
            try {
              inputRef.current.setSelectionRange(
                selectionStateRef.current.start,
                selectionStateRef.current.end
              );
            } catch (e) {
              // Ignore errors
            }
          }
        }
        // Clear flags after all focus restoration attempts
        isPerformingSearchUpdateRef.current = false;
        selectionStateRef.current = null;
      }, 100);
    }, 500);
  }, [pushIndexUiState, filters, contextQuery]);
  
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // Track if we're on mobile (md and down, which is < 768px)
  const checkMobile = useCallback(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);
  
  useEffect(() => {
    // Check on mount
    checkMobile();
  }, [checkMobile]);
  
  // Use throttled resize to prevent lag
  useThrottledResize(checkMobile);

  // Mobile portaled autocomplete: lock main SRP scroll so pans scroll suggestions, not the page.
  useEffect(() => {
    if (typeof document === 'undefined' || !popUp) return;
    const root = document.documentElement;
    if (isOpen && isFocused) {
      root.dataset.autocompleteOpen = 'true';
      return () => {
        delete root.dataset.autocompleteOpen;
      };
    }
    delete root.dataset.autocompleteOpen;
  }, [popUp, isOpen, isFocused]);

  // Update popover position - always calculate width to prevent size animation
  useEffect(() => {
    if (inputRef.current && containerRef.current) {
      const updatePosition = () => {
        if (inputRef.current && containerRef.current) {
          const inputRect = inputRef.current.getBoundingClientRect();
          const containerRect = containerRef.current.getBoundingClientRect();

          if (popUp) {
            // Position above the input - calculate from bottom of layout viewport
            const viewportHeight = window.innerHeight;
            const vv = window.visualViewport;
            let spaceAbove = inputRect.top - 8;
            if (vv) {
              spaceAbove = inputRect.top - vv.offsetTop - 8;
            }
            const maxHeightPx = Math.min(384, Math.max(80, Math.floor(spaceAbove)));
            setPopoverPosition({
              top: viewportHeight - inputRect.top, // used as CSS `bottom` distance from viewport bottom
              left: isMobile ? 0 : inputRect.left,
              width: isMobile ? window.innerWidth : inputRect.width,
              maxHeightPx,
            });
          } else {
            // Position below the input - use container-relative positioning so the dropdown
            // always aligns with the input at any screen width (avoids misalignment from
            // flex layout / getBoundingClientRect rounding at certain breakpoints)
            const topOffset = inputRect.bottom - containerRect.top; // Distance from container top to input bottom
            setPopoverPosition({
              top: topOffset,
              left: 0, // Always align to container; use width 100% in styles
              width: containerRect.width, // Match container width for consistent alignment
              maxHeightPx: 384,
            });
          }
        }
      };
      // Always calculate position immediately to set width
      updatePosition();
      // Only add scroll/resize listeners when popover is visible to avoid unnecessary calculations
      if (isOpen && isFocused) {
        // Throttle resize handler to prevent lag
        let resizeTicking = false;
        const throttledResize = () => {
          if (!resizeTicking) {
            resizeTicking = true;
            updatePosition();
            requestAnimationFrame(() => {
              resizeTicking = false;
            });
          }
        };

        const vv = typeof window !== 'undefined' ? window.visualViewport : null;
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', throttledResize, { passive: true });
        if (vv && popUp) {
          vv.addEventListener('resize', updatePosition);
          vv.addEventListener('scroll', updatePosition);
        }
        return () => {
          window.removeEventListener('scroll', updatePosition, true);
          window.removeEventListener('resize', throttledResize);
          if (vv && popUp) {
            vv.removeEventListener('resize', updatePosition);
            vv.removeEventListener('scroll', updatePosition);
          }
        };
      }
    }
  }, [isOpen, isFocused, popUp, isMobile]);

  // Expose close method via ref
  useImperativeHandle(ref, () => ({
    close: () => {
      setIsOpen(false);
      setIsFocused(false);
      inputRef.current?.blur();
      wasFocusedBeforeSearchRef.current = false;
    },
  }));

  // Handle input focus/blur for popover and highlight
  const handleFocus = () => {
    setIsOpen(true);
    setIsFocused(true);
    refreshFacetSuggestions(inputValue);
    // Track that input is focused for maintaining keyboard on mobile
    wasFocusedBeforeSearchRef.current = true;
    // Notify parent that autocomplete opened
    onOpen?.();
  };
  
  // Prevent blur during search updates by maintaining focus
  const handleMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
    // If we're performing a search update, prevent any clicks from stealing focus
    if (isPerformingSearchUpdateRef.current) {
      e.preventDefault();
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };
  
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // If we're performing a search update, prevent blur from closing the popover
    // by immediately refocusing the input
    if (isPerformingSearchUpdateRef.current && inputRef.current) {
      // Use setTimeout(0) to refocus after the blur event completes
      setTimeout(() => {
        if (inputRef.current && isPerformingSearchUpdateRef.current) {
          inputRef.current.focus();
        }
      }, 0);
      return;
    }
    
    // Use a small delay to check if focus moved to the popover
    // This allows clicking on facet suggestions without closing the popover
    setTimeout(() => {
      // Only close if input is actually not focused anymore (user clicked elsewhere)
      // This check prevents closing when focus is restored after search update
      if (document.activeElement !== inputRef.current && 
          !containerRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
        setIsFocused(false);
        wasFocusedBeforeSearchRef.current = false;
      }
    }, 150); // Increased timeout to give more time for focus restoration
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    // Update local input state immediately (for responsive input display)
    setInputValue(newValue);
    
    // Fetch facet suggestions immediately (for autocomplete dropdown)
    refreshFacetSuggestions(newValue);
    
    // Debounce the actual search/navigation update (500ms after user stops typing)
    debouncedSearchUpdate(newValue);
    
    // Keep input focused
    if (inputRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleFacetSuggestionClick = (option: FacetAutocompleteOption) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    trackSearchSuggestion({ suggestion: option.label, locale: intl.locale });

    const currentFilterValues = filtersObj[option.attribute] || [];
    if (currentFilterValues.includes(option.value)) {
      setIsOpen(false);
      inputRef.current?.blur();
      return;
    }

    const nextFilters: Record<string, string[]> = {
      ...filtersObj,
      [option.attribute]: [...currentFilterValues, option.value],
    };

    // Clear the search input and query when a facet is selected
    setInputValue('');
    setIsOpen(false);
    inputRef.current?.blur();
    pushIndexUiState({
      query: '', // Clear the search query
      refinementList: nextFilters,
      page: 0,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      // Clear any pending debounced search
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      
      setIsOpen(false);
      inputRef.current?.blur();
      // Immediately update search when Enter is pressed (no debounce)
      pushIndexUiState({ query: inputValue.trim(), refinementList: filters as any, page: 0 });
    }
  };

  // Handle clear button click
  const handleClear = () => {
    // Clear any pending debounced search
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    // Update local input value
    setInputValue('');
    setFacetSuggestions(filterFacetAutocompleteOptions(facetOptions, '', FACET_SUGGESTION_LIMIT));
    setIsOpen(false);
    
    // Immediately clear the search, reset to page 0 (0-indexed: page 0 = first page), and reset sort to default
    pushIndexUiState({ query: '', refinementList: filters as any, page: 0, sort: 'relevance' });
    
    // Keep focus on input after clearing
    inputRef.current?.focus();
  };
  
  // Show clear button if there's an input value, we're on a page other than 0, or sort is not default
  const showClearButton = inputValue || page > 0 || sort !== 'relevance';

  // Focus input on '/' key press
  const focusInputOnSlash = useCallback((e: KeyboardEvent) => {
    // Only trigger if not focused in an input/textarea/select
    if (
      e.key === '/' &&
      !(document.activeElement instanceof HTMLInputElement) &&
      !(document.activeElement instanceof HTMLTextAreaElement) &&
      !(document.activeElement instanceof HTMLSelectElement)
    ) {
      e.preventDefault();
      inputRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', focusInputOnSlash);
    return () => window.removeEventListener('keydown', focusInputOnSlash);
  }, [focusInputOnSlash]);

  return (
    <div ref={containerRef} className={'relative w-full z-[100] ' + (className || '')} data-component="autocomplete">
      {/* Search icon, absolutely positioned */}
      <MagnifyingGlass weight="fill" className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A6E5F] dark:text-[#8A7060] pointer-events-none z-10" />
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        suppressHydrationWarning
        placeholder={intl.formatMessage({
          id: 'instantsearch.searchbox.placeholder', defaultMessage: 'Search jobs, language, or city', description: 'Placeholder text for search input box',
        })}
        className={
          `pl-11 pr-10 py-3 backdrop-blur-xl text-[#1E1A14] dark:text-[#F2EADF] font-medium focus:outline-none w-full text-base placeholder:text-[#7A6E5F] dark:placeholder:text-[#8A7060] transition-all duration-300 ease-out ${
            isOpen && facetSuggestions.length > 0
              ? popUp
                ? 'bg-white/95 dark:bg-[#1F1509]/95 border-l-2 border-r-2 border-b-2 border-[#DDD5C8]/80 dark:border-[#4A3520]/90 rounded-b-3xl rounded-t-none'
                : 'bg-white/95 dark:bg-[#1F1509]/95 border-l-2 border-r-2 border-t-2 border-[#DDD5C8]/80 dark:border-[#4A3520]/90 rounded-t-3xl rounded-b-none'
              : isFocused
                ? 'bg-white/90 dark:bg-[#1F1509]/90 border-2 border-[#3B5E45]/45 dark:border-[#98CFA8]/35 rounded-3xl'
                : 'bg-white/90 dark:bg-[#1F1509]/90 border border-[#DDD5C8]/70 dark:border-[#4A3520]/80 rounded-3xl'
          } ${
            isFocused
              ? isOpen && facetSuggestions.length > 0
                ? 'ring-2 ring-[#3B5E45]/20 dark:ring-[#98CFA8]/20'
                : 'ring-2 ring-[#3B5E45]/30 dark:ring-[#98CFA8]/25'
              : 'ring-0'
          } ${pulse ? ' animate-[pulse_1.2s_ease-in-out_3] ring-2 ring-[#3B5E45]/40 dark:ring-[#98CFA8]/30' : ''}` +
          ' font-[system-ui,-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,Helvetica,Arial,sans-serif]'
        }
        style={{
          backdropFilter: isOpen && facetSuggestions.length > 0 
            ? 'blur(24px) saturate(180%)'
            : 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: isOpen && facetSuggestions.length > 0
            ? 'blur(24px) saturate(180%)'
            : 'blur(20px) saturate(180%)',
          boxShadow: 'inset 0 2px 8px 0 rgba(0, 0, 0, 0.06), inset 0 1px 2px 0 rgba(0, 0, 0, 0.04)',
        }}
        autoComplete="off"
      />
      {/* Clear button - shows when there's text or on page > 0 */}
      {showClearButton && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full bg-[#F2EADF] dark:bg-[#2A1D0F] hover:bg-[#EDE3D4] dark:hover:bg-[#342415] active:scale-95 text-[#3D3529] dark:text-[#C4A882] transition-all duration-200 cursor-pointer backdrop-blur-sm shadow-sm dark:shadow-none"
          aria-label="Clear search"
        >
          <X className="w-4 h-4" />
        </button>
      )}
      {/* '/' badge - shows when input is empty and on page 0 */}
      {!showClearButton && (
        <span
          className="absolute right-3 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-full bg-[#F2EADF] dark:bg-[#2A1D0F] text-[#5A4A3A] dark:text-[#C4A882] text-xs font-mono font-semibold select-none pointer-events-none border border-[#DDD5C8] dark:border-[#4A3520] backdrop-blur-sm shadow-sm dark:shadow-none opacity-90"
          aria-label="Shortcut: / to focus search"
        >
          /
        </span>
      )}
      {/* Custom popover for suggestions */}
      {(() => {
        const popoverContent = (
          <div
            data-autocomplete-popover={popUp ? 'mobile' : undefined}
            className={
              `${popUp ? 'fixed' : 'absolute'} backdrop-blur-xl bg-white/95 dark:bg-[#1F1509]/95 z-[110] min-h-0 overflow-y-auto overscroll-y-contain touch-pan-y [-webkit-overflow-scrolling:touch] transition-all duration-300 ease-out ${
                popUp ? 'max-h-[min(24rem,100dvh)]' : 'max-h-96'
              } ${
                popUp
                  ? 'rounded-t-3xl rounded-b-none border-l-2 border-r-2 border-t-2 border-[#DDD5C8]/80 dark:border-[#4A3520]/90'
                  : 'rounded-b-3xl rounded-t-none border-l-2 border-r-2 border-b-2 border-[#DDD5C8]/80 dark:border-[#4A3520]/90'
              }` +
              (isOpen && isFocused
                ? 'opacity-100 scale-y-100 visible'
                : 'opacity-0 scale-y-95 invisible pointer-events-none')
            }
            style={{
              // Use container-relative sizing for non-popUp so dropdown stays aligned at any screen width
              width: popUp
                ? (isMobile ? '100vw' : (popoverPosition.width > 0 ? `${popoverPosition.width}px` : 'auto'))
                : (isMobile ? '100vw' : '100%'),
              ...(popUp
                ? {
                    maxHeight: `${popoverPosition.maxHeightPx}px`,
                    ...(popoverPosition.width > 0
                      ? {
                          bottom: `${popoverPosition.top}px`, // Position exactly at top edge of input (no gap)
                          left: isMobile ? '0' : `${popoverPosition.left}px`,
                        }
                      : {}),
                  }
                : {}),
              ...(!popUp ? {
                top: `${popoverPosition.top}px`,
                left: '0', // Always align to container to avoid overflow/misalignment at certain widths
              } : {}),
              transformOrigin: popUp ? 'bottom' : 'top',
              willChange: 'transform, opacity',
              backdropFilter: 'blur(24px) saturate(180%)',
              WebkitBackdropFilter: 'blur(24px) saturate(180%)',
              boxShadow: 'inset 0 4px 14px 0 rgba(0, 0, 0, 0.12), inset 0 1px 4px 0 rgba(0, 0, 0, 0.08)',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent',
            }}
            suppressHydrationWarning
          >
        {isOpen && isFocused && (
          <div className="relative py-2">
            {facetSuggestions.length > 0 ? (
              <>
                {/* Scroll gradient indicators */}
                <div className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-white/95 dark:from-[#1F1509]/95 to-transparent pointer-events-none z-10" />
                <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-white/95 dark:from-[#1F1509]/95 to-transparent pointer-events-none z-10" />
                <div className="px-5 pt-3 pb-2 text-xs text-[#7A6E5F] dark:text-[#B8A48C] font-semibold uppercase tracking-wider flex items-center justify-between">
                  <span>{intl.formatMessage({ id: 'autocomplete.filters', defaultMessage: 'Filter matches', description: 'Section header in autocomplete dropdown showing filter suggestions that match the search query' })}</span>
                  {facetSuggestions.length > 5 && (
                    <span className="text-[10px] normal-case font-normal text-[#7A6E5F] dark:text-[#8A7060]">
                      {intl.formatMessage({ id: 'autocomplete.scrollHint', defaultMessage: 'Scroll for more', description: 'Hint text shown when there are more filter suggestions available that can be viewed by scrolling' })}
                    </span>
                  )}
                </div>
                {facetSuggestions.map((item) => (
                  <button
                    key={`${item.attribute}:${item.value}`}
                    className="flex w-full items-center justify-between gap-3 text-left px-5 py-3 text-xs text-[#1E1A14] dark:text-[#F2EADF] hover:bg-[#F2EADF]/80 dark:hover:bg-[#2A1D0F]/90 active:bg-[#EDE3D4] dark:active:bg-[#342415] transition-all duration-200 font-medium cursor-pointer"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleFacetSuggestionClick(item)}
                  >
                    <span className="flex flex-col">
                      <span className="text-xs uppercase tracking-wide text-[#7A6E5F] dark:text-[#B8A48C] font-semibold">
                        {item.attributeLabel}
                      </span>
                      <span>{item.label}</span>
                    </span>
                    <span className="text-sm text-[#7A6E5F] dark:text-[#B8A48C] font-semibold">
                      {item.count.toLocaleString(intl.locale)}
                    </span>
                  </button>
                ))}
              </>
            ) : (
              <div className="px-5 py-4 text-center text-sm text-[#7A6E5F] dark:text-[#B8A48C]">
                {intl.formatMessage({ 
                  id: 'autocomplete.noMatches', 
                  defaultMessage: 'No filter matches found',
                  description: 'Message shown in autocomplete dropdown when no filter suggestions match the search query'
                })}
              </div>
            )}
          </div>
        )}
          </div>
        );

        // Use portal for mobile (popUp) to escape overflow-hidden containers
        if (popUp && typeof window !== 'undefined') {
          return createPortal(popoverContent, document.body);
        }
        return popoverContent;
      })()}
    </div>
  );
});

Autocomplete.displayName = 'Autocomplete';
